import { useIntl } from 'react-intl';
import { PropsWithChildren } from 'react';
import { Check, Calendar, Pencil } from 'lucide-react';
import { Button, Avatar, AvatarFallback } from '@hqo/ui-atoms';
import { Modal } from 'components/shared/modal/modal';
import { VisitorAvatar } from 'components/shared/visitor-avatar/visitor-avatar';
import { Visit, VisitStatus } from 'store/visits/types';
import { useFloorEnabled, useSuiteEnabled, useVisitorCompanyEnabled } from 'store/ui-metadata/hooks';
import { useDateTimeFormatters } from 'shared/hooks/use-date-time-formatters/use-date-time-formatters.hook';
import { useCheckInOption } from 'components/visit-modal/visit-details/check-in/check-in.hooks';
import { useFlag } from 'hooks/use-flag.hook';
import { LDFlags, getStatusColor, getStatusGlyph, getStatusHeadingId } from 'shared/consts';
import { useScheduleText } from 'shared/hooks/use-schedule-text';
import { useIsActivityPane } from 'components/activity-pane-provider';
import { ConfirmationModalSkeleton } from './confirmation-modal-skeleton';

/**
 * The frame around the visit body: the swipe modal standalone, a plain full-pane wrapper in the
 * activity-center pane. In the pane the record is a persistent detail view, not something to dismiss,
 * so the swipe-modal chrome (drag handle, backdrop, close) has no job — it just needs a container that
 * fills the pane and scrolls (VIS-2032).
 */
const VisitDetailFrame = ({
  isActivityPane,
  onClose,
  children,
}: PropsWithChildren<{ isActivityPane: boolean; onClose: VoidFunction }>): JSX.Element =>
  isActivityPane ? (
    <div data-testid="visit-pane" className="fixed inset-0 z-40 overflow-y-auto bg-white px-4 pb-6">
      {children}
    </div>
  ) : (
    <Modal onClose={onClose} variant="normal" withExitButton marginTop="100px">
      {children}
    </Modal>
  );

interface ConfirmationModalV2Props {
  visit: Visit | null;
  onClose: VoidFunction;
  onEditDate?: VoidFunction;
  onCancelVisit?: VoidFunction;
  /**
   * Render the heading for this status instead of the confirmation heading.
   *
   * Omitted by the post-create route, which is a success screen and says so whatever the visit later
   * becomes. Supplied by the detail route, which has to describe a visit in any state.
   */
  status?: VisitStatus;
  /**
   * Whether this caller may use the full-pane framing when it lands in the activity centre.
   *
   * Set by the detail route, which is a persistent record with no need for the swipe-modal chrome. Kept
   * separate from `status` on purpose: `status` only arrives once the visit loads, and framing must be
   * decided before then — otherwise the frame flips modal -> pane the moment the fetch returns, remounting
   * the swipe modal and replaying its open animation (the "double swipe" stutter). This is a fixed,
   * per-route flag, so the frame stays put while the body swaps skeleton -> visit.
   */
  paneEligible?: boolean;
}

export const ConfirmationModalV2 = ({
  visit,
  onClose,
  onEditDate,
  onCancelVisit,
  status,
  paneEligible = false,
}: ConfirmationModalV2Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const floorEnabled = useFloorEnabled();
  const suiteEnabled = useSuiteEnabled();
  const visitorCompanyEnabled = useVisitorCompanyEnabled();
  const { formatDate, formatTimeRange } = useDateTimeFormatters();
  const showUiMetadata = useFlag(LDFlags.SHOW_VISIT_FORM_UI_METADATA, false);
  const isActivityPane = useIsActivityPane();
  // Pane framing is for the DETAIL route only, which is the status-bearing one the activity centre
  // deeplinks to. The post-create confirmation reuses this component without `paneEligible`; it must stay
  // a dismissible modal with its "Back to visits" exit even in the pane, since Edit/Cancel are
  // conditional and don't guarantee a way out (VIS-2032). `paneEligible` is a fixed per-route flag rather
  // than `status !== undefined`, so the frame does not flip once the visit loads (VIS: double-swipe).
  const isPaneDetail = isActivityPane && paneEligible;
  const visitTypeId = visit?.visit_type_id != null ? String(visit.visit_type_id) : null;
  const checkInOptionLabel = useCheckInOption(visitTypeId);
  const scheduleText = useScheduleText(visit);

  // Without a status this is the post-create success screen, which says "Visit confirmed" and shows the
  // tick regardless — that is what it is reporting. With one, the heading describes the visit instead.
  const statusColor = status ? getStatusColor(status) : undefined;
  const StatusGlyph = status ? getStatusGlyph(status) : Check;
  const statusTitle = status
    ? formatMessage({ id: getStatusHeadingId(status), defaultMessage: 'Visit' })
    : formatMessage({ id: 'confirmationModal.visitConfirmed', defaultMessage: 'Visit confirmed' });

  if (!visit) {
    // Not "nothing yet" — the same frame, holding a skeleton. Rendering the frame here (rather than
    // returning null and letting the caller mount a separate loading modal) is what keeps the swipe modal
    // mounted across the fetch: it opens once, then the body swaps skeleton -> visit in place. Kept the
    // status computations above the guard so they stay hook-order-stable; the visit-derived ones below it
    // only run once there is a visit to derive them from.
    return (
      <VisitDetailFrame isActivityPane={isPaneDetail} onClose={onClose}>
        <ConfirmationModalSkeleton />
      </VisitDetailFrame>
    );
  }

  const showFloor = visit.floor && floorEnabled;
  const showSuite = visit.suite && suiteEnabled;
  const isVendorOrGroupVisit = Boolean(visit.vendor_company_uuid) || Boolean(visit.is_group_visit);
  const canShowEditDate = onEditDate && visit.visitors?.length === 1 && !isVendorOrGroupVisit;

  const isAllDayVisit = (): boolean => {
    if (!visit.arrival_date || !visit.departure_date) {
      return false;
    }
    const arrival = new Date(visit.arrival_date);
    const departure = new Date(visit.departure_date);
    return (
      arrival.getHours() === 0 &&
      arrival.getMinutes() === 0 &&
      departure.getHours() === 23 &&
      departure.getMinutes() === 59
    );
  };

  const getTimeDisplay = (): string => {
    if (isAllDayVisit()) {
      return formatMessage({
        id: 'labels.allDayVisit',
        defaultMessage: 'All day visit',
      });
    }

    return formatTimeRange(
      new Date(visit.arrival_date),
      visit.departure_date ? new Date(visit.departure_date) : undefined,
    );
  };

  return (
    <VisitDetailFrame isActivityPane={isPaneDetail} onClose={onClose}>
      <div className="flex flex-col items-center w-full pt-6">
        <div className="flex flex-col items-center gap-3 text-center mb-8">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              statusColor ? '' : 'bg-green-500'
            }`}
            style={statusColor ? { backgroundColor: statusColor } : undefined}
          >
            <StatusGlyph size={24} color="white" />
          </div>
          <h2
            className="text-2xl font-semibold m-0 text-gray-900"
            data-cy="visit-status-title"
            data-testid="visit-status-title"
          >
            {statusTitle}
          </h2>
        </div>

        <div className="w-full border border-gray-200 rounded-lg p-6 flex flex-col gap-6">
          <h3 className="text-lg font-semibold m-0 text-gray-900">
            {formatMessage({
              id: 'labels.visitDetails',
              defaultMessage: 'Visit details',
            })}
          </h3>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar size={16} color="#6b7280" />
                  <span className="font-medium text-sm text-gray-900" data-cy="confirm-date">
                    {visit.series_dates && visit.series_dates.length > 1
                      ? visit.series_dates.map((date) => formatDate(new Date(`${date}T00:00:00`))).join(', ')
                      : formatDate(new Date(visit.arrival_date))}
                  </span>
                </div>
                {canShowEditDate && (
                  <Button
                    variant="ghost"
                    onClick={onEditDate}
                    className="h-8 w-8 p-0 min-w-8"
                    data-testid="confirm-edit-date"
                  >
                    <Pencil size={16} />
                  </Button>
                )}
              </div>
              <p className="text-sm text-gray-500 m-0 ml-6" data-cy="confirm-time">
                {getTimeDisplay()}
              </p>
              {scheduleText && (
                <p className="text-sm text-gray-500 m-0 ml-6" data-cy="confirm-recurrence">
                  {scheduleText}
                </p>
              )}
              {onCancelVisit && (
                <button
                  type="button"
                  onClick={onCancelVisit}
                  className="text-sm text-red-600 bg-transparent border-none p-0 ml-6 cursor-pointer text-left underline decoration-transparent hover:decoration-red-600 transition-colors"
                >
                  {formatMessage({
                    id: 'confirmationModal.cancelVisit',
                    defaultMessage: 'Cancel visit',
                  })}
                </button>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-900">
                {formatMessage(
                  { id: 'labels.visitorNames', defaultMessage: 'Visitor name(s)' },
                  { type: formatMessage({ id: 'labels.visitor.types.guest', defaultMessage: 'Visitor' }) },
                )}
              </span>
              <div className="flex flex-col gap-2">
                {visit.visitors?.length ? (
                  visit.visitors.map((visitor, index) => (
                    <div key={visitor.id || index} className="flex items-center gap-3">
                      <VisitorAvatar
                        firstName={visitor.first_name}
                        lastName={visitor.last_name}
                        imageUrl={visitor.image_url}
                        photoUuid={visitor.photo_uuid}
                        size="sm"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900" data-cy="confirm-visitor-names">
                          {visitor.first_name} {visitor.last_name}
                        </span>
                        {visitorCompanyEnabled && visitor.company && (
                          <span className="text-xs text-gray-600">{visitor.company}</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                        {visit.first_name?.[0]?.toUpperCase() || ''}
                        {visit.last_name?.[0]?.toUpperCase() || ''}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-gray-900" data-cy="confirm-visitor-names">
                      {visit.first_name} {visit.last_name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Host section */}
            {visit.host && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {formatMessage({
                    id: 'labels.host',
                    defaultMessage: 'Host',
                  })}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-900">
                    {visit.host.first_name} {visit.host.last_name}
                  </span>
                  {visit.company_name && <span className="text-xs text-gray-600">{visit.company_name}</span>}
                </div>
              </div>
            )}

            {/* Visit summary section */}
            {visit.description && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {formatMessage({
                    id: 'labels.visitSummary',
                    defaultMessage: 'Visit summary',
                  })}
                </span>
                <p className="text-sm text-gray-900 m-0">{visit.description}</p>
              </div>
            )}

            {/* Check-in type section */}
            {showUiMetadata && checkInOptionLabel && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {formatMessage({
                    id: 'createVisitFormV2.visitStep.checkInType',
                    defaultMessage: 'Check-in type',
                  })}
                </span>
                <p className="text-sm text-gray-900 m-0" data-cy="confirm-check-in-type">
                  {checkInOptionLabel}
                </p>
              </div>
            )}

            {/* Location section */}
            {(showFloor || showSuite) && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {formatMessage({
                    id: 'labels.location',
                    defaultMessage: 'Location',
                  })}
                </span>
                <span className="text-sm text-gray-900">
                  {showFloor && `Floor ${visit.floor}`}
                  {showFloor && showSuite && ', '}
                  {showSuite && visit.suite}
                </span>
              </div>
            )}
          </div>

          {/* A dismiss control for the standalone modal; in the pane the record is persistent and
              there is nothing to go back to, so it is dropped (VIS-2032). */}
          {!isPaneDetail && (
            <Button className="w-full mt-2" onClick={onClose} data-cy="back-to-visits">
              {formatMessage({
                id: 'button.backToVisits',
                defaultMessage: 'Back to visits',
              })}
            </Button>
          )}
        </div>
      </div>
    </VisitDetailFrame>
  );
};
